'use client';

import type { ProviderResponse } from '@/app/models/providersResponse';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import formatterPercentualValue from '@/app/utils/percentualUtils';
import TruncatedTextWithTooltip from '@/app/utils/truncatedTextWithTooltip';
import DataTable, { bodyCellSx, Column } from '@/components/common/DataTable';
import { TableCell } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type EventTableParams = {
  rows: ProviderResponse[];
};

export default function ProcessingTable(props: EventTableParams) {
  const navigate = useNavigate();
  const { t } = useTranslation(['provider', 'common']);
  const { rows } = props;

  const columns: Column[] = [
    { key: 'resourceDonor', label: t('resource.donor') },
    { key: 'allocationProject', label: t('allocation.project') },
    { key: 'branch', label: t('common:branch') },
    { key: 'total', label: `${t('total')}` },
    { key: 'available', label: `${t('available')}` },
    { key: 'balance', label: `${t('balance')}` },
    { key: 'extra', label: `${t('extra')}` },
    { key: 'priority', label: t('priority'), align: 'center' },
    { key: 'eligible', label: `${t('eligible')}`, align: 'center' },
    { key: 'maxAllocation', label: `${t('max.allocation')}`, align: 'center' },
  ];

  const renderRow = (row: ProviderResponse) => (
    <>
      <TableCell sx={bodyCellSx}>
        <TruncatedTextWithTooltip value={`${row.provider.code} - ${row.provider.name}`} size={37} sx={bodyCellSx} />
      </TableCell>
      <TableCell sx={bodyCellSx}>
        <TruncatedTextWithTooltip value={`${row.toAllocate.code} - ${row.toAllocate.name}`} size={37} sx={bodyCellSx} />
      </TableCell>
      <TableCell sx={bodyCellSx}>
        <TruncatedTextWithTooltip value={`${row.provider.branchName}`} size={37} sx={bodyCellSx} />
      </TableCell>
      <TableCell sx={bodyCellSx}>{`${formatterMonetaryValue(row.totalValue)}`}</TableCell>
      <TableCell sx={bodyCellSx}>{`${formatterMonetaryValue(row.availableValue)}`}</TableCell>
      <TableCell sx={bodyCellSx}>{`${formatterMonetaryValue(row.balanceValue)}`}</TableCell>
      <TableCell sx={bodyCellSx}>{`${formatterMonetaryValue(row.extraValue)}`}</TableCell>
      <TableCell align="center" sx={bodyCellSx}>{`${row.priority}`}</TableCell>
      <TableCell align="center" sx={bodyCellSx}>{`${formatterPercentualValue(row.eligiblePercent)}`}</TableCell>
      <TableCell align="center" sx={bodyCellSx}>{`${formatterPercentualValue(row.maxAllocation)}`}</TableCell>
    </>
  );

  // Use provider ID and period for unique key
  const getRowKey = (row: ProviderResponse) => `${row.provider.id}-${row.period}`;

  return (
    <DataTable
      columns={columns}
      data={rows}
      renderRow={renderRow}
      getRowKey={getRowKey}
      emptyMessage={t('common:no.data.for.the.chosen.period.and.search.term')}
      onRowClick={(row) => navigate(`${row.provider.id}`)}
    />
  );
}
